import { useState, useEffect, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { format, startOfMonth, endOfMonth } from 'date-fns';
import {
  Container,
  Grid,
  Typography,
  Stack,
  Card,
  Alert,
  Box,
  Table,
  TableRow,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  CircularProgress
} from '@material-ui/core';
import { useDispatch } from 'react-redux';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import Page from '../components/Page';
import Breadcrumb from '../components/common/Breadcrumb';
import TeamPartNumbersTable from '../components/billing/TeamPartNumbersTable';
import MonthPicker, {
  formatMonthParam,
  initialMonthFromUrl
} from '../components/billing/MonthPicker';
import RefreshInvoiceButton from '../components/billing/RefreshInvoiceButton';
import Scrollbar from '../components/Scrollbar';
import SearchNotFound from '../components/SearchNotFound';
import { UserListHead, UserListToolbar } from '../components/user';
import { formatCurrency } from '../utils/formatCurrency';
import useVrackApi from '../hooks/useVrackApi';
import { setOpenSnackbar } from '../store/slices/snackbarSlice';
import ExportButton from '../components/billing/ExportButton';

const DIRECTORY_TABLE_HEAD = [
  { id: 'team_name', label: 'Team ID', alignRight: false },
  { id: 'company', label: 'Company', alignRight: false },
  { id: 'billable_items_count', label: 'Billable Quantity', alignRight: false },
  { id: 'team_usage_cost', label: 'Team Usage Cost', alignRight: true }
];

export default function TeamUsageReport() {
  const { teamId } = useParams();
  const navigate = useNavigate();
  // Seed the picker from the URL `?month=YYYY-MM` so the directory view and
  // the team detail view share the same selected month when the user lands
  // here via a click that carried the param (e.g. from the company billing
  // report or from another team page). Falls back to the current month.
  const [searchParams] = useSearchParams();
  const [selectedMonth, setSelectedMonth] = useState(() => initialMonthFromUrl(searchParams));

  if (teamId) {
    return (
      <TeamDetail
        teamId={teamId}
        selectedMonth={selectedMonth}
        onMonthChange={setSelectedMonth}
        navigate={navigate}
      />
    );
  }

  return (
    <TeamDirectory
      selectedMonth={selectedMonth}
      onMonthChange={setSelectedMonth}
      navigate={navigate}
    />
  );
}

function TeamDirectory({ selectedMonth, onMonthChange, navigate }) {
  const [page, setPage] = useState(0);
  const [filterName, setFilterName] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [teams, setTeams] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);

  const dispatch = useDispatch();
  const openSnackbar = (payload) => dispatch(setOpenSnackbar(payload));
  const { fetchData: fetchTeamUsage } = useVrackApi('get', '/billing/teams/usage');

  // /billing/teams/usage expects YYYY-MM (e.g. "2026-07"); format the picked
  // Date directly so we don't keep a label<->value translation table.
  const apiMonth = selectedMonth ? format(selectedMonth, 'yyyy-MM') : '';

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setIsError(false);
    fetchTeamUsage({ params: { month: apiMonth } })
      .then((response) => {
        if (cancelled) return;
        // Response shape: { billing_month, team_count, teams: [{ team_name, company_id, company_name, project_code, manager, team_usage_cost, billable_items_count }, ...] }
        const list = Array.isArray(response?.teams) ? response.teams : [];
        setTeams(list);
      })
      .catch((err) => {
        if (cancelled) return;
        setIsError(true);
        openSnackbar({
          message: err.response?.data?.message || 'Failed to load team usage report',
          severity: 'error'
        });
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [apiMonth]);

  const allTeams = useMemo(
    () =>
      teams.map((team) => ({
        // `team_name` is the team's identifier per the API spec — it's
        // also what the directory table shows as "Team ID" and what we
        // use to navigate into the detail page.
        id: team.team_name,
        name: team.team_name,
        companyId: team.company_id,
        company: team.company_name || team.company_id || '',
        projectCode: team.project_code || '',
        managerEmail: team.manager || '',
        billableItemsCount: team.billable_items_count ?? 0,
        usageCost: team.team_usage_cost ?? 0
      })),
    [teams]
  );

  const filteredTeams = useMemo(() => {
    if (!filterName) return allTeams;
    const searchLower = filterName.toLowerCase();
    return allTeams.filter(
      (team) =>
        team.name.toLowerCase().includes(searchLower) ||
        team.company.toLowerCase().includes(searchLower)
    );
  }, [allTeams, filterName]);

  const handleFilterByName = (event) => {
    setFilterName(event.target.value);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const isNotFound = filteredTeams.length === 0;

  return (
    <Page title="Team Usage | Virtual Racks">
      <Container maxWidth={false}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              spacing={2}
            >
              <Stack spacing={0}>
                <Typography variant="h4">Team Usage</Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Select a team to open its dedicated Team Usage Report.
                </Typography>
              </Stack>
              <Stack direction="row" spacing={2} alignItems="center">
                <MonthPicker value={selectedMonth} onChange={onMonthChange} />
              </Stack>
            </Stack>
          </Grid>

          <Grid item xs={12}>
            <Card>
              <UserListToolbar
                placeholder="Search team or company..."
                filterName={filterName}
                onFilterName={handleFilterByName}
              />
              {isError ? (
                <Box sx={{ p: 3 }}>
                  <Alert severity="error">Failed to load team usage report.</Alert>
                </Box>
              ) : (
                <Scrollbar>
                  <TableContainer sx={{ minWidth: 900 }}>
                    <Table>
                      <UserListHead
                        order="asc"
                        orderBy="team_name"
                        headLabel={DIRECTORY_TABLE_HEAD}
                        rowCount={allTeams.length}
                        onRequestSort={() => {}}
                      />
                      <TableBody>
                        {isLoading ? (
                          <TableRow>
                            <TableCell align="center" colSpan={4} sx={{ py: 3 }}>
                              <CircularProgress size={24} />
                            </TableCell>
                          </TableRow>
                        ) : (
                          <>
                            {filteredTeams
                              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                              .map((row) => {
                                // Carry the selected month into the team detail
                                // URL so the destination's MonthPicker initializes
                                // to the same month the user was viewing here —
                                // otherwise it snaps back to the current month.
                                const monthParam = formatMonthParam(selectedMonth);
                                const target = `/team-usage/${row.id}`;
                                const url = monthParam ? `${target}?month=${monthParam}` : target;
                                return (
                                  <TableRow
                                    hover
                                    key={row.id}
                                    sx={{ cursor: 'pointer' }}
                                    onClick={() => navigate(url)}
                                  >
                                    <TableCell align="left">
                                      <strong style={{ color: '#0067b2' }}>{row.id}</strong>
                                    </TableCell>
                                    <TableCell align="left">{row.company}</TableCell>
                                    <TableCell align="left">{row.billableItemsCount}</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                                      {formatCurrency(row.usageCost)}
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            {isNotFound && (
                              <TableRow>
                                <TableCell align="center" colSpan={4} sx={{ py: 3 }}>
                                  <SearchNotFound searchQuery={filterName} />
                                </TableCell>
                              </TableRow>
                            )}
                          </>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Scrollbar>
              )}
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={allTeams.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </Card>
          </Grid>
        </Grid>
      </Container>
    </Page>
  );
}

function TeamDetail({ teamId, selectedMonth, onMonthChange, navigate }) {
  // Read all params so handlePartNumberClick can detect whether the team
  // page was reached via the company-scoped route and keep the breadcrumb
  // chain intact.
  const params = useParams();
  const [report, setReport] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  // 404 from /billing/teams/{team}/usage means the team has no usage
  // recorded for the requested month — render an empty state instead of an
  // error.
  const [isNotFound, setIsNotFound] = useState(false);

  const dispatch = useDispatch();
  const openSnackbar = (payload) => dispatch(setOpenSnackbar(payload));
  const { fetchData: fetchTeamBilling } = useVrackApi(
    'get',
    `/billing/teams/${encodeURIComponent(teamId)}/usage`
  );

  // `MonthPicker` returns a Date; render it in the same "Month YYYY" shape
  // the rest of the page expects (matches the prior MonthPicker labels).
  const billingMonthLabel = selectedMonth ? format(selectedMonth, 'MMMM yyyy') : '';
  const apiMonth = selectedMonth ? format(selectedMonth, 'yyyy-MM') : '';

  // Refetch the team usage report — used both by the month effect below
  // and by the Regenerate button after POSTing a fresh invoice for the
  // team's company.
  const refetchTeamBilling = useCallback(() => {
    let cancelled = false;
    setIsLoading(true);
    setIsError(false);
    setIsNotFound(false);
    fetchTeamBilling({ params: { month: apiMonth } })
      .then((response) => {
        if (cancelled) return;
        setReport(response || null);
      })
      .catch((err) => {
        if (cancelled) return;
        const status = err.response?.status;
        if (status === 404) {
          setIsNotFound(true);
          return;
        }
        setIsError(true);
        openSnackbar({
          message: err.response?.data?.message || 'Failed to load team billing report',
          severity: 'error'
        });
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [apiMonth, teamId]);

  useEffect(() => {
    const cleanup = refetchTeamBilling();
    return cleanup;
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [apiMonth, teamId]);

  // Map API billable_parts onto the shape TeamPartNumbersTable already
  // expects. `profile` and `description` aren't returned by the API, so we
  // leave them empty strings — the table's search filter is case-insensitive
  // and tolerates empty fields.
  const partNumbers = useMemo(
    () =>
      Array.isArray(report?.billable_parts)
        ? report.billable_parts.map((part) => ({
            id: part.part_number,
            profile: '',
            description: '',
            billableQuantity: part.billable_quantity ?? 0,
            unitPrice: part.unit_price ?? 0,
            monthlyCharge: part.part_total_cost ?? 0
          }))
        : [],
    [report]
  );

  const handlePartNumberClick = (part) => {
    // When the team page is reached via the company-scoped route
    // (/billing/company/:companyId/team/:teamId), nest the part URL under
    // the company too so the breadcrumb chain survives the click. Falls
    // back to the legacy /team-usage/:teamId URL for users who reach the
    // page directly via the sidebar. The selected month is appended via
    // `?month=YYYY-MM` so TeamPartNumberTracking's MonthPicker initializes to
    // the same month the user was viewing here. TeamPartNumberTracking now
    // fetches its own data by month, so we no longer pass `partData` /
    // `teamName` through React Router state.
    const monthParam = formatMonthParam(selectedMonth);
    const partPath = params.companyId
      ? `/billing/company/${params.companyId}/team/${teamId}/part/${encodeURIComponent(part.id)}`
      : `/team-usage/${teamId}/part/${encodeURIComponent(part.id)}`;
    navigate(monthParam ? `${partPath}?month=${monthParam}` : partPath);
  };

  const displayName = report?.team_name || teamId;
  const teamUsageCost = report?.team_usage_cost ?? 0;
  // The team's usage report is a slice of the parent company's invoice.
  // Regenerate POSTs to /invoice for the team's company so the next refetch
  // reflects the freshly-generated invoice.
  const companyId = report?.company_id;
  const monthStartDate = selectedMonth ? format(startOfMonth(selectedMonth), 'yyyy-MM-dd') : '';
  const monthEndDate = selectedMonth ? format(endOfMonth(selectedMonth), 'yyyy-MM-dd') : '';

  return (
    <Page title={`${displayName} Usage Report | Virtual Racks`}>
      <Container maxWidth={false}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Breadcrumb
              labels={{ [teamId]: displayName }}
              // On the company-scoped URL (`/billing/company/:companyId/team/:teamId`),
              // `company` and `team` are route delimiters — drop them so the
              // trail shows the entities only: Home → Billing → {companyId} →
              // {teamId}. On the legacy /team-usage/:teamId URL, no exclusions.
              exclude={params.companyId ? ['company', 'team'] : []}
              sx={{ mb: 2 }}
            />
          </Grid>

          {/* Error banner — pinned high so it's the first thing the user sees. */}
          {isError && (
            <Grid item xs={12}>
              <Alert severity="error">Failed to load team billing report.</Alert>
            </Grid>
          )}

          {/* Team header — overline + h4 mirrors the layout used by the
              Part Number Deployment page so the two detail pages
              share a single visual language. */}
          <Grid item xs={12}>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'flex-start', md: 'center' }}
              spacing={2}
            >
              <Stack>
                <Typography variant="overline" color="primary">
                  Team Usage Report
                </Typography>
                <Typography variant="h4">{displayName}</Typography>
              </Stack>
              <Stack direction="row" spacing={2} alignItems="center">
                <RefreshInvoiceButton
                  companyId={companyId}
                  startDate={monthStartDate}
                  endDate={monthEndDate}
                  onRefreshed={refetchTeamBilling}
                />
                <ExportButton invoiceData={report} />
                <MonthPicker value={selectedMonth} onChange={onMonthChange} />
              </Stack>
            </Stack>
          </Grid>

          {/* Informational banner */}
          <Grid item xs={12}>
            <Alert severity="info">
              <strong>Informational allocation only.</strong> This report is not a separate invoice.
              It shows the share of company charges attributed to billable LRUs deployed under this
              team.
            </Alert>
          </Grid>

          {/* Meta cards */}
          <Grid item xs={12}>
            <Grid container spacing={2}>
              <MetaCard label="Team Usage Cost" value={formatCurrency(teamUsageCost)} />
              <MetaCard label="Company" value={companyId || '—'} />
              <MetaCard label="Billing Month" value={billingMonthLabel} />
            </Grid>
          </Grid>

          {/* Inline empty state for 404 (no usage recorded this month) */}
          {isNotFound && (
            <Grid item xs={12}>
              <Alert severity="info">
                No usage recorded for <strong>{displayName}</strong> in {billingMonthLabel}.
              </Alert>
            </Grid>
          )}

          {/* Loading state — placeholder for the parts table while fetching */}
          {isLoading && (
            <Grid item xs={12}>
              <Card>
                <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
                  <CircularProgress size={24} />
                </Box>
              </Card>
            </Grid>
          )}

          {/* LRU (part number) table */}
          {!isLoading && !isError && !isNotFound && (
            <Grid item xs={12}>
              <TeamPartNumbersTable
                partNumbers={partNumbers}
                onPartNumberClick={handlePartNumberClick}
              />
            </Grid>
          )}
        </Grid>
      </Container>
    </Page>
  );
}

function MetaCard({ label, value }) {
  return (
    <Grid item xs={12} sm={6} md={4}>
      <Card variant="outlined" sx={{ padding: 2 }}>
        <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase' }}>
          {label}
        </Typography>
        <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 500 }}>
          {value}
        </Typography>
      </Card>
    </Grid>
  );
}

MetaCard.propTypes = {
  label: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

MetaCard.defaultProps = {
  label: '',
  value: ''
};

TeamDirectory.propTypes = {
  selectedMonth: PropTypes.instanceOf(Date),
  onMonthChange: PropTypes.func,
  navigate: PropTypes.func
};

TeamDetail.propTypes = {
  teamId: PropTypes.string,
  selectedMonth: PropTypes.instanceOf(Date),
  onMonthChange: PropTypes.func,
  navigate: PropTypes.func
};
